import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowUpRight, Shield, Cpu, Radio, Search, Award, MapPin, Lock, Compass, Zap, FileCheck,
} from "lucide-react";
import { SERVICES, INDUSTRIES, WHY_CHOOSE } from "@/data/services";
import { SectionKicker, SectionHeading, FadeIn, Stat } from "@/components/shared/SectionBits";
import { api } from "@/lib/api";

const ICONS = { shield: Shield, cpu: Cpu, radio: Radio, search: Search,
    award: Award, "map-pin": MapPin, lock: Lock, compass: Compass, zap: Zap, "file-check": FileCheck };

const HERO_IMG = "https://customer-assets-jai6qajn.emergentagent.net/job_dynamic-protect-dev/artifacts/inpgonk3_Main%20website%20image.png";
const SPLIT_IMG = "https://customer-assets-jai6qajn.emergentagent.net/job_dynamic-protect-dev/artifacts/1rzq73bm_Meeting%201.png";

export default function HomePage() {
    const [posts, setPosts] = useState([]);

    useEffect(() => {
        api.get("/posts", { params: { type: "blog", limit: 3 } })
            .then((r) => setPosts(r.data))
            .catch(() => setPosts([]));
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-[92vh] flex items-end overflow-hidden">
                <div className="absolute inset-0">
                    <motion.img
                        src={HERO_IMG}
                        alt="London financial district skyline"
                        initial={{ scale: 1.08 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-dr-navy/95 via-dr-navy/80 to-dr-navy/30" />
                    <div className="absolute inset-0 bg-gradient-to-b from-dr-navy/20 via-transparent to-dr-navy/40" />
                </div>

                <div className="relative dr-container w-full pb-20 pt-32">
                    <div className="max-w-3xl">
                        <motion.p
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.15, duration: 0.7 }}
                            className="text-xs tracking-[0.36em] uppercase text-dr-blue font-medium"
                        >
                            Corporate risk · Protective security · Cyber
                            <br />
                            Security Consultancy · Investigations
                        </motion.p>
                        <motion.h1
                            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.25, duration: 0.85 }}
                            className="font-display text-white font-bold text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight mt-6"
                        >
                            Protecting people,{" "}
                            <span className="text-dr-blue">information</span>{" "}
                            and reputation.
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4, duration: 0.7 }}
                            className="text-white/80 text-lg md:text-xl mt-7 max-w-2xl leading-relaxed"
                        >
                            Dynamic Risks provides specialist consultancy in security, investigations,
                            cyber resilience and technical surveillance countermeasures.
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.55, duration: 0.7 }}
                            className="flex flex-wrap gap-3 mt-9"
                        >
                            <Link
                                to="/services"
                                data-testid="hero-cta-explore"
                                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-dr-navy font-semibold hover:bg-dr-blue transition-colors"
                            >
                                Explore services
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/contact"
                                data-testid="hero-cta-speak"
                                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/40 text-white font-semibold hover:bg-white hover:text-dr-navy transition-colors"
                            >
                                Speak to an expert
                            </Link>
                        </motion.div>
                    </div>

                    {/* Stat strip */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 mt-20 max-w-4xl">
                        {[
                            { v: "24/7", l: "Incident response" },
                            { v: "UK", l: "Headquartered" },
                            { v: "150+", l: "Board engagements" },
                            { v: "ISO", l: "27001 · 22301 aligned" },
                        ].map((s) => (
                            <div key={s.l} className="border-t border-white/30 pt-4">
                                <div className="font-display text-3xl md:text-4xl text-white font-bold">{s.v}</div>
                                <div className="text-[11px] uppercase tracking-[0.24em] text-white/60 mt-2">{s.l}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="dr-container py-28 md:py-36">
                <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
                    <div className="md:col-span-7">
                        <SectionKicker>What we do</SectionKicker>
                        <SectionHeading>Four disciplines. One integrated view of risk.</SectionHeading>
                    </div>
                    <div className="md:col-span-5">
                        <p className="text-dr-dark/75 text-lg leading-relaxed">
                            We combine physical, cyber and investigative expertise so decisions are made once, on
                            evidence, and executed with confidence.
                        </p>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {SERVICES.map((s, i) => {
                        const Icon = ICONS[s.icon] || Shield;
                        return (
                            <FadeIn key={s.slug} delay={i * 0.08}>
                                <Link
                                    to={`/services/${s.slug}`}
                                    data-testid={`service-card-${s.slug}`}
                                    className="group block h-full bg-white border border-dr-silver rounded-2xl p-8 hover:-translate-y-1 hover:shadow-[0_20px_60px_rgba(8,41,74,0.12)] transition-[transform,box-shadow] duration-500"
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-dr-navy text-white group-hover:bg-dr-blue transition-colors">
                                            <Icon className="w-5 h-5" strokeWidth={2} />
                                        </span>
                                        <ArrowUpRight className="w-5 h-5 text-dr-dark/40 group-hover:text-dr-navy group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                                    </div>
                                    <h3 className="font-display text-2xl font-bold text-dr-navy mt-8 leading-tight">{s.title}</h3>
                                    <p className="text-sm text-dr-dark/70 mt-3 leading-relaxed">{s.short}</p>
                                    <ul className="mt-6 space-y-1.5">
                                        {(s.subServices || s.items).slice(0, 4).map((it) => (
                                            <li key={it.title} className="text-xs text-dr-dark/60 flex gap-2">
                                                <span className="w-1 h-1 rounded-full bg-dr-blue mt-1.5" />
                                                {it.title}
                                            </li>
                                        ))}
                                    </ul>
                                </Link>
                            </FadeIn>
                        );
                    })}
                </div>
            </section>

            {/* WHY CHOOSE — split */}
            <section className="bg-dr-grey py-28 md:py-36">
                <div className="dr-container grid lg:grid-cols-12 gap-14 items-start">
                    <div className="lg:col-span-5 lg:sticky lg:top-28">
                        <div className="relative rounded-2xl overflow-hidden aspect-[4/5] shadow-[0_30px_80px_rgba(8,41,74,0.18)]">
                            <img src={SPLIT_IMG} alt="Corporate advisers reviewing a document" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-dr-navy/40 to-transparent" />
                            <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur rounded-xl p-5 border border-white">
                                <p className="text-xs uppercase tracking-[0.28em] text-dr-blue font-medium">Confidential</p>
                                <p className="text-dr-navy font-display font-semibold mt-2 leading-snug">
                                    Discretion is designed into every engagement, from first call to final report.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <SectionKicker>Why Dynamic Risks</SectionKicker>
                        <SectionHeading>A strategic partner, not just a security supplier.</SectionHeading>
                        <p className="text-dr-dark/75 text-lg mt-6 max-w-xl leading-relaxed">
                            The polished feel of a top-tier consultancy, with the operational depth of practitioners
                            drawn from senior policing, the armed forces, regulatory and the &ldquo;Big Four&rdquo; backgrounds.
                        </p>

                        <div className="grid sm:grid-cols-2 gap-6 mt-12">
                            {WHY_CHOOSE.map((w, i) => {
                                const Icon = ICONS[w.icon] || Award;
                                return (
                                    <FadeIn key={w.title} delay={i * 0.06}>
                                        <div className="border border-dr-silver rounded-xl bg-white p-6 h-full">
                                            <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-dr-navy/5 text-dr-navy">
                                                <Icon className="w-5 h-5" />
                                            </span>
                                            <h4 className="font-display text-lg font-semibold text-dr-navy mt-5">{w.title}</h4>
                                            <p className="text-sm text-dr-dark/70 mt-2 leading-relaxed">{w.desc}</p>
                                        </div>
                                    </FadeIn>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* INDUSTRIES */}
            <section className="dr-container py-28 md:py-36">
                <div className="grid md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-6">
                        <SectionKicker>Industries served</SectionKicker>
                        <SectionHeading>Advising leaders across regulated and complex sectors.</SectionHeading>
                    </div>
                    <div className="md:col-span-6">
                        <p className="text-dr-dark/75 text-lg leading-relaxed">
                            From listed corporates to private clients and government, our engagements are as varied
                            as the risks they address.
                        </p>
                    </div>
                </div>
                <div className="mt-14 grid grid-cols-2 md:grid-cols-3 border-t border-l border-dr-silver">
                    {INDUSTRIES.map((ind, i) => (
                        <div
                            key={ind}
                            data-testid={`industry-${ind.toLowerCase().replace(/\s+/g, "-")}`}
                            className="group relative border-b border-r border-dr-silver p-8 md:p-10 hover:bg-dr-navy transition-colors duration-500"
                        >
                            <span className="text-xs text-dr-dark/40 group-hover:text-dr-blue transition-colors font-mono">
                                {String(i + 1).padStart(2, "0")}
                            </span>
                            <p className="mt-4 font-display text-xl md:text-2xl font-semibold text-dr-navy group-hover:text-white transition-colors">
                                {ind}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* LATEST NEWS */}
            <section className="bg-dr-navy py-28 md:py-36 dr-grain">
                <div className="dr-container">
                    <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
                        <div className="md:col-span-8">
                            <p className="text-xs tracking-[0.32em] uppercase text-dr-blue mb-4 font-medium">Latest insights</p>
                            <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-tight tracking-tight">
                                Analysis for boards, general counsel and risk leaders.
                            </h2>
                        </div>
                        <div className="md:col-span-4 flex md:justify-end">
                            <Link
                                to="/blog"
                                data-testid="home-view-all-insights"
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/30 text-white hover:bg-white hover:text-dr-navy transition-colors"
                            >
                                All insights
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {posts.slice(0, 3).map((p, i) => (
                            <FadeIn key={p.slug} delay={i * 0.08}>
                                <Link
                                    to={`/blog/${p.slug}`}
                                    data-testid={`home-post-${p.slug}`}
                                    className="group block h-full bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 transition-colors"
                                >
                                    <div className="aspect-[16/10] overflow-hidden">
                                        <img
                                            src={p.cover_image}
                                            alt={p.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="p-7">
                                        <p className="text-[11px] tracking-[0.24em] uppercase text-dr-blue font-medium">{p.category}</p>
                                        <h3 className="font-display text-xl font-semibold text-white mt-3 leading-snug">{p.title}</h3>
                                        <p className="text-sm text-white/70 mt-3 leading-relaxed line-clamp-2">{p.excerpt}</p>
                                        <p className="text-[11px] uppercase tracking-widest text-white/40 mt-5">
                                            {p.read_minutes} min read
                                        </p>
                                    </div>
                                </Link>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
